import { useMemo } from "react";
import { t } from "ttag";

import { useSetting } from "metabase/common/hooks";
import { useSelector } from "metabase/lib/redux";
import type { ParameterValueWidgetProps } from "metabase/parameters/components/ParameterValueWidget";
import { getParameterWidgetTitle } from "metabase/parameters/utils/ui";
import { BooleanWidget } from "metabase/querying/parameters/components/BooleanWidget";
import { DateAllOptionsWidget } from "metabase/querying/parameters/components/DateAllOptionsWidget";
import { DateMonthYearWidget } from "metabase/querying/parameters/components/DateMonthYearWidget";
import { DateQuarterYearWidget } from "metabase/querying/parameters/components/DateQuarterYearWidget";
import { DateRangeWidget } from "metabase/querying/parameters/components/DateRangeWidget";
import { DateRelativeWidget } from "metabase/querying/parameters/components/DateRelativeWidget";
import { DateSingleWidget } from "metabase/querying/parameters/components/DateSingleWidget";
import type {
  FieldFilterUiParameter,
  UiParameter,
} from "metabase-lib/v1/parameters/types";
import { getNumberParameterArity } from "metabase-lib/v1/parameters/utils/operators";
import {
  getFields,
  hasFields,
} from "metabase-lib/v1/parameters/utils/parameter-fields";
import { getQueryType } from "metabase-lib/v1/parameters/utils/parameter-source";
import {
  isBooleanParameter,
  isDateParameter,
  isNumberParameter,
  isTemporalUnitParameter,
} from "metabase-lib/v1/parameters/utils/parameter-type";
import { getIsMultiSelect } from "metabase-lib/v1/parameters/utils/parameter-values";
import type { State } from "metabase-types/store";

import { NumberInputWidget } from "./widgets/NumberInputWidget";
import { ParameterFieldWidget } from "./widgets/ParameterFieldWidget/ParameterFieldWidget";
import { StringInputWidget } from "./widgets/StringInputWidget";
import { TemporalUnitWidget } from "./widgets/TemporalUnitWidget";
import { TextWidget } from "./widgets/TextWidget";

type ParameterDropdownWidgetProps = {
  onFocusChanged: (focused: boolean) => void;
  onPopoverClose?: () => void;
} & ParameterValueWidgetProps;

export const ParameterDropdownWidget = ({
  parameter,
  value,
  setValue,
  onPopoverClose,
  className,
  isEditing,
  commitImmediately,
  placeholder,
  onFocusChanged,
  parameters,
  cardId,
  dashboardId,
  enableRequiredBehavior,
}: ParameterDropdownWidgetProps) => {
  const reportTimezone = useSetting("report-timezone");
  const systemTimezone = useSetting("system-timezone");
  const databases = useSelector((state) => state.entities.databases);
  const questions = useSelector((state) => state.entities.questions);
  const tables = useSelector((state) => state.entities.tables);
  const datePickerTimezone = useMemo(
    () =>
      getDatePickerTimezone(
        parameter,
        cardId,
        reportTimezone,
        systemTimezone,
        databases,
        questions,
        tables,
      ),
    [
      parameter,
      cardId,
      reportTimezone,
      systemTimezone,
      databases,
      questions,
      tables,
    ],
  );
  const normalizedValue = Array.isArray(value)
    ? value
    : [value].filter((v) => v != null);

  // TODO this is due to some widgets not supporting focusChanged callback.
  const setValueOrDefault = (value: any) => {
    const { required, default: defaultValue } = parameter;
    const shouldUseDefault =
      enableRequiredBehavior && required && defaultValue && !value?.length;

    setValue(shouldUseDefault ? defaultValue : value);
    onPopoverClose?.();
  };

  if (isDateParameter(parameter)) {
    const DateWidget = {
      "date/single": DateSingleWidget,
      "date/range": DateRangeWidget,
      "date/month-year": DateMonthYearWidget,
      "date/quarter-year": DateQuarterYearWidget,
      "date/relative": DateRelativeWidget,
      "date/all-options": DateAllOptionsWidget,
    }[parameter.type];

    if (DateWidget) {
      const timezoneProps =
        DateWidget === DateAllOptionsWidget ? datePickerTimezone : {};

      return (
        <DateWidget
          value={value}
          availableOperators={["=", ">", "<", "between", "!="]}
          submitButtonLabel={value ? t`Update filter` : t`Add filter`}
          {...timezoneProps}
          onChange={(value) => {
            setValue?.(value);
            onPopoverClose?.();
          }}
        />
      );
    }

    return null;
  }

  if (isTemporalUnitParameter(parameter)) {
    return (
      <TemporalUnitWidget
        parameter={parameter}
        value={value}
        setValue={setValue}
        onClose={() => onPopoverClose?.()}
      />
    );
  }

  if (isBooleanParameter(parameter)) {
    return (
      <BooleanWidget
        value={value}
        submitButtonLabel={value ? t`Update filter` : t`Add filter`}
        onChange={(value) => {
          setValue?.(value);
          onPopoverClose?.();
        }}
      />
    );
  }

  if (isTextWidget(parameter)) {
    return (
      <TextWidget
        value={value}
        setValue={setValue}
        className={className}
        isEditing={isEditing}
        commitImmediately={commitImmediately}
        placeholder={placeholder}
        focusChanged={onFocusChanged}
      />
    );
  }

  if (isNumberParameter(parameter) && getQueryType(parameter) !== "list") {
    const arity = getNumberParameterArity(parameter);

    return (
      <NumberInputWidget
        value={normalizedValue}
        setValue={setValueOrDefault}
        arity={arity}
        infixText={typeof arity === "number" && arity > 1 ? t`and` : undefined}
        autoFocus
        placeholder={isEditing ? t`Enter a default value…` : undefined}
        label={getParameterWidgetTitle(parameter)}
        parameter={parameter}
      />
    );
  }

  if (isFieldWidget(parameter)) {
    return (
      <ParameterFieldWidget
        parameter={parameter}
        parameters={parameters}
        cardId={cardId}
        dashboardId={dashboardId}
        value={normalizedValue}
        fields={parameter.fields}
        setValue={setValueOrDefault}
        isEditing={isEditing}
      />
    );
  }

  return (
    <StringInputWidget
      className={className}
      parameter={parameter}
      value={normalizedValue}
      setValue={setValueOrDefault}
      label={getParameterWidgetTitle(parameter)}
      placeholder={isEditing ? t`Enter a default value…` : undefined}
      autoFocus
      isMultiSelect={getIsMultiSelect(parameter)}
    />
  );
};

export function isTextWidget(parameter: UiParameter) {
  const canQuery = getQueryType(parameter) !== "none";
  const isMultiSelect = getIsMultiSelect(parameter);
  return parameter.hasVariableTemplateTagTarget && !canQuery && !isMultiSelect;
}

function isFieldWidget(
  parameter: UiParameter,
): parameter is FieldFilterUiParameter {
  const canQuery = getQueryType(parameter) !== "none";

  return parameter.hasVariableTemplateTagTarget
    ? canQuery
    : canQuery || hasFields(parameter);
}

type DatePickerTimezone = {
  timezone?: string;
  timezoneLabel?: string;
};

type DatabasesById = State["entities"]["databases"];
type QuestionsById = State["entities"]["questions"];
type TablesById = State["entities"]["tables"];

function getDatePickerTimezone(
  parameter: UiParameter,
  cardId: ParameterValueWidgetProps["cardId"],
  reportTimezone: string | null | undefined,
  systemTimezone: string | null | undefined,
  databasesById: DatabasesById,
  questionsById: QuestionsById,
  tablesById: TablesById,
): DatePickerTimezone {
  const databases = getUniqueDatabases(
    parameter,
    cardId,
    databasesById,
    questionsById,
    tablesById,
  );

  if (
    reportTimezone &&
    (databases.length === 0 || databases.every(databaseSupportsReportTimezone))
  ) {
    return {
      timezone: reportTimezone,
      timezoneLabel: reportTimezone,
    };
  }

  const databaseTimezones = getUniqueTimezones(
    databases.map((database) => database.timezone),
  );

  if (databaseTimezones.length === 1) {
    return {
      timezone: databaseTimezones[0],
      timezoneLabel: databaseTimezones[0],
    };
  }

  if (databaseTimezones.length > 1) {
    return getBrowserTimezone();
  }

  const timezone = systemTimezone || getBrowserTimezone().timezone;

  return {
    timezone,
    timezoneLabel: timezone,
  };
}

type DatePickerDatabase = {
  id?: number | string;
  name?: string;
  timezone?: string | null;
  features?: string[];
};

type DatePickerTable = {
  id?: number | string;
  db_id?: number;
  database?: DatePickerDatabase | null;
  db?: DatePickerDatabase | number | null;
};

function getUniqueDatabases(
  parameter: UiParameter,
  cardId: ParameterValueWidgetProps["cardId"],
  databasesById: DatabasesById,
  questionsById: QuestionsById,
  tablesById: TablesById,
) {
  let databases = getFields(parameter)
    .map((field) => getFieldDatabase(field, databasesById, tablesById))
    .filter((database): database is NonNullable<typeof database> =>
      Boolean(database),
    );

  if (databases.length === 0) {
    const database = getQuestionDatabase(cardId, databasesById, questionsById);
    databases = database ? [database] : [];
  }

  return Array.from(
    new Map(databases.map((database) => [database.id, database])).values(),
  );
}

function getFieldDatabase(
  field: ReturnType<typeof getFields>[number],
  databasesById: DatabasesById,
  tablesById: TablesById,
): DatePickerDatabase | null | undefined {
  const fieldTable = field.table as DatePickerTable | undefined;
  const fieldTableDatabase = getTableDatabase(fieldTable, databasesById);
  if (fieldTableDatabase) {
    return fieldTableDatabase;
  }

  const tableId = field.table_id ?? fieldTable?.id;
  const table = tableId != null ? tablesById[tableId] : undefined;

  return getTableDatabase(table as DatePickerTable | undefined, databasesById);
}

function getQuestionDatabase(
  cardId: ParameterValueWidgetProps["cardId"],
  databasesById: DatabasesById,
  questionsById: QuestionsById,
): DatePickerDatabase | null | undefined {
  if (cardId == null) {
    return null;
  }

  const question = questionsById[cardId];
  const databaseId = question?.dataset_query?.database ?? question?.database_id;

  return databaseId != null ? databasesById[databaseId] : null;
}

function getTableDatabase(
  table: DatePickerTable | undefined,
  databasesById: DatabasesById,
): DatePickerDatabase | null | undefined {
  if (!table) {
    return null;
  }

  if (table.database) {
    return table.database;
  }

  if (isDatePickerDatabase(table.db)) {
    return table.db;
  }

  const databaseId = typeof table.db === "number" ? table.db : table.db_id;

  return databaseId != null ? databasesById[databaseId] : null;
}

function isDatePickerDatabase(
  database: DatePickerDatabase | number | null | undefined,
): database is DatePickerDatabase {
  return typeof database === "object" && database != null;
}

function getUniqueTimezones(timezones: Array<string | null | undefined>) {
  return Array.from(new Set(timezones.filter(Boolean))) as string[];
}

function databaseSupportsReportTimezone(database: { features?: string[] }) {
  return database.features?.includes("set-timezone") === true;
}

function getBrowserTimezone(): DatePickerTimezone {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    timezone,
    timezoneLabel: t`Browser timezone: ${timezone}`,
  };
}
